import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";

export interface StatusOption<T extends string> {
  value: T;
  label: string;
  /** Badge classes for the trigger and for the option's pill in the menu. */
  badgeClass: string;
}

export interface StatusDropdownProps<T extends string> {
  value: T | undefined;
  options: readonly StatusOption<T>[];
  onChange: (next: T) => Promise<void>;
  /**
   * What the control does, e.g. "Update session review status". The trigger
   * announces it with the current status appended, so the value is both spoken
   * and contained in the accessible name.
   */
  ariaLabel: string;
  /** Toast text if the write fails. */
  errorMessage: string;
  /** In-flight elsewhere (a shared mutation hook), on top of this control's own write. */
  disabled?: boolean;
}

/**
 * Badge-styled status picker. The session's review status and a turn's review
 * status sit a few pixels apart in this view, so they render through one
 * component rather than two copies of the same markup.
 */
export const StatusDropdown = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  errorMessage,
  disabled,
}: StatusDropdownProps<T>) => {
  const { showError } = useToast();
  const [isWriting, setIsWriting] = useState(false);

  // An unknown or absent value reads as the first option, which every caller
  // orders as its neutral default.
  const selected = options.find((option) => option.value === value) ?? options[0];
  const isBlocked = isWriting || disabled === true;

  const handleSelect = async (next: T) => {
    // N.B. this guard rather than a `disabled` trigger. A disabled trigger drops
    // keyboard focus to `<body>` when Radix hands it back on close, and never
    // gets it back, so `aria-disabled` carries the state and this stops the write.
    if (isBlocked || next === selected.value) return;

    setIsWriting(true);
    try {
      await onChange(next);
    } catch (error) {
      // The trigger falls back to the persisted value on failure, which on its
      // own looks like nothing happened.
      console.error(error);
      showError(errorMessage);
    } finally {
      setIsWriting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          // N.B. `ghost`, not the default variant: every bit of this trigger's
          // paint comes from `badgeClass`, and the default's `hover:bg-primary/90`
          // outlives any badge that carries no `hover:bg-*` of its own, turning a
          // tinted chip brand purple with unreadable text under the cursor.
          variant="ghost"
          size="sm"
          // N.B. `w-fit` so a caller stacking this in a flex column gets a badge
          // rather than a trigger stretched across the column.
          className={cn(
            "w-fit gap-1 border text-xs font-medium aria-disabled:pointer-events-none aria-disabled:opacity-50",
            selected.badgeClass
          )}
          aria-label={`${ariaLabel}: ${selected.label}`}
          aria-disabled={isBlocked}
          aria-busy={isWriting}
        >
          <span>{selected.label}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* A radio group so the current status is marked by `aria-checked` and an
            indicator glyph. Tinting the row instead would say it in colour only,
            in the same colour the focused row already uses. */}
        <DropdownMenuRadioGroup value={selected.value}>
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              onClick={() => void handleSelect(option.value)}
            >
              {/* `border` because every caller's `badgeClass` carries a border
                  colour and no width, and the pill's fill matches the focused
                  row's own in light mode. */}
              <span
                className={cn(
                  "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                  option.badgeClass
                )}
              >
                {option.label}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
